import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DataTablePagination,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipProvider,
} from '@cargomatic/ui';
import { getFilterParams, useLaneRates } from 'hooks/useLaneRate';
import { showToast } from 'stores/ToastStore';
import WebAPIUtils from 'utils/AccessorialRequestWebAPIUtils';
import { t } from 'i18next';

import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useModal from 'hooks/useModal';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontal,
} from 'lucide-react';
import AuditTrailModal from 'components/modules/pricing/modals/LaneRateAuditTrailModal';
import { LaneRatesCsvUploadModal } from 'components/modules/pricing/LaneRatesCsvUploadModal';
import qs from 'qs';
import { getColumns } from './columns';
import { EditRateSequenceModal } from './EditRateSequenceModal';
import { DataTableToolbar } from './DataTableToolbar';
import SessionStore from 'stores/SessionStore';
import { useLocalStorage } from 'hooks/useLocalStorage';

const highlighted = {
  rateCustomer: true,
  rateMarket: true,
  rateEquipmentType: true,
  rateEquipmentSize: true,
  rateHazardous: true,
  rateOrderType: true,
  rateSteamshipOrRailroad: true,
};

const paginationDefaults = {
  pageSize: 10,
  pageIndex: 0,
};

const columnVisibilityDefaults = {
  rateCustomer: false,
  rateEquipmentType: false,
  rateEquipmentSize: false,
  rateHazardous: false,
  rateOrderType: false,
  rateSteamshipOrRailroad: false,
};

export default function LaneRatesRateConditions() {
  const columns = getColumns();
  const [hideRateConditionColumns, setHideRateConditionColumns] =
    useState(true);
  const [columnVisibility, setColumnVisibility] = useState({
    ...columnVisibilityDefaults,
  });
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    ...paginationDefaults,
  });
  const { visible, show, close } = useModal();
  const [filters, setFilters] = useState([]);
  const [selectedRateSequence, setSelectedRateSequence] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [rateAudit, setRateAudit] = useState(null);
  const [showLaneRatesCsvUploadModal, setLaneRatesCsvUploadModalIsOpen] =
    useState(false);

  const [tableState, setTableState] = useLocalStorage(
    'laneRateRateConditionTableState',
  );

  useEffect(() => {
    setFilters(tableState?.filters ?? []);
    setSorting(tableState?.sorting ?? []);
    setPagination(tableState?.pagination ?? { ...paginationDefaults });
    setColumnVisibility(
      tableState?.columnVisibility ?? { ...columnVisibilityDefaults },
    );
    setHideRateConditionColumns(tableState?.hideRateConditionColumns ?? true);
  }, []);

  useEffect(() => {
    setTableState({
      filters,
      sorting,
      pagination,
      columnVisibility,
      hideRateConditionColumns,
    });
  }, [
    filters,
    sorting,
    pagination,
    columnVisibility,
    hideRateConditionColumns,
  ]);

  const history = useHistory();

  const { data, isLoading, refetch } = useLaneRates({
    sorting,
    pagination,
    filters,
  });

  const selectedRowCount = useMemo(() => {
    return Object.keys(rowSelection).length;
  }, [rowSelection]);

  function handleFiltersChange(updater) {
    const newFilters = updater(filters);
    setFilters(newFilters);
    setRowSelection({});
    setPagination({
      ...pagination,
      pageIndex: 0,
    });
  }

  function canUpdate() {
    const isShipperLanePricingManager =
      SessionStore.isShipperLanePricingManager();
    const isCarrierLanePricingManager =
      SessionStore.isCarrierLanePricingManager();
    if (
      (isShipperLanePricingManager || isCarrierLanePricingManager) &&
      !(isShipperLanePricingManager && isCarrierLanePricingManager)
    ) {
      return (
        (selectedRateSequence?.customerType === 'Shipper' &&
          isShipperLanePricingManager) ||
        (selectedRateSequence?.customerType === 'Carrier' &&
          isCarrierLanePricingManager)
      );
    }

    return SessionStore.isAbleToEditLaneRate();
  }

  function handleRowSelectionChange(selection) {
    setRowSelection(selection);
  }

  function handleSortChange(updater) {
    const newValue = updater(sorting);
    setSorting(newValue);
    setPagination({ pageSize: pagination.pageSize, pageIndex: 0 });
    setRowSelection({});
  }

  function onEditRateSequence(laneRate) {
    setSelectedRateSequence(laneRate);
    show();
  }

  function handleResequenceRatesSuccess() {
    refetch();
    close();
  }

  // Once we update the edit page to use query strings, update to a link with lane rate ids
  function onEditLaneRate(laneRate) {
    const queryString = qs.stringify({ id: laneRate.id });

    history.push({
      pathname: '/lane-rates-v2/edit',
      search: `?${queryString}`,
    });
  }

  function onBulkEditRateSequence() {
    const selectedLaneRates = Object.keys(rowSelection).map((index) => {
      return {
        ...data?.laneRates?.[index],
        weeklyCommitment: data?.laneRates?.[index]?.commitment,
      };
    });
    const selectedLaneRateIds = selectedLaneRates.map((lr) => lr.id);
    const searchParams = new URLSearchParams();
    selectedLaneRateIds.forEach((id) => searchParams.append('id', id));
    const queryString = searchParams.toString();

    history.push({
      pathname: '/lane-rates-v2/edit',
      search: `?${queryString}`,
    });
  }

  function handleAuditClick(laneRate) {
    setRateAudit(laneRate);
    setShowAuditTrailModal(true);
  }

  function handleBulkUploadClick() {
    setLaneRatesCsvUploadModalIsOpen(true);
  }

  async function exportLaneRates() {
    const searchParams = getFilterParams({ sorting, pagination, filters });

    try {
      await WebAPIUtils.exportUserLaneRatesWithConditions(searchParams);
      showToast(t('CSV_DOWNLOAD_SUCCESSFUL'), null, 'success');
    } catch (error) {
      showToast(t('CSV_DOWNLOAD_FAILED'), error, 'error');
    }
  }

  function renderViewRateCard(laneRate) {
    const requiredDoc = laneRate?.document || laneRate?.oldDocument;
    const url = requiredDoc?.signedUrl || '';

    if (requiredDoc && url) {
      return (
        <DropdownMenuItem>
          <Link to={{ pathname: url }} target="_blank" className="text-black">
            {t('VIEW_RATE_CARD')}
          </Link>
        </DropdownMenuItem>
      );
    }
  }

  const mappedColumnDefs = useMemo(() => {
    return columns.concat({
      id: 'actions',
      cell: ({ row }) => {
        const payment = row.original;

        return (
          <div className="text-right">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-8 h-8 p-0">
                  <span className="sr-only">{t('OPEN_MENU')}</span>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t('ACTIONS')}</DropdownMenuLabel>
                <TooltipProvider>
                  <Tooltip
                    content={
                      canUpdate() ? '' : 'You do not have permission to edit'
                    }
                  >
                    <DropdownMenuItem
                      disabled={!canUpdate()}
                      onClick={() => onEditRateSequence(payment)}
                    >
                      {t('EDIT_RATE_SEQUENCE')}
                    </DropdownMenuItem>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip
                    content={
                      canUpdate() ? '' : 'You do not have permission to edit'
                    }
                  >
                    <DropdownMenuItem
                      disabled={!canUpdate()}
                      onClick={() => onEditLaneRate(payment)}
                    >
                      {t('EDIT_LANE_RATE')}
                    </DropdownMenuItem>
                  </Tooltip>
                </TooltipProvider>
                <DropdownMenuSeparator />
                {renderViewRateCard(payment)}
                <DropdownMenuItem onClick={() => handleAuditClick(payment)}>
                  {t('VIEW_AUDIT_TRAIL')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    });
  }, []);

  function handlePaginationChange(selection) {
    setPagination(selection);
    setRowSelection({});
  }

  const options = {
    columns: mappedColumnDefs,
    data: data?.laneRates || [],
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnVisibility,
      sorting,
      pagination,
      rowSelection,
      columnFilters: filters,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: handleSortChange,
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnFiltersChange: handleFiltersChange,
  };
  const table = useReactTable({
    ...options,
    manualSorting: true,
    manualPagination: true,
    rowCount: data?.count,
    manualFiltering: true,
  });

  useEffect(() => {
    if (hideRateConditionColumns) {
      setColumnVisibility({
        rateCustomer: false,
        rateEquipmentType: false,
        rateEquipmentSize: false,
        rateHazardous: false,
        rateOrderType: false,
        rateSteamshipOrRailroad: false,
      });
    } else {
      setColumnVisibility({
        rateCustomer: true,
        rateEquipmentType: true,
        rateEquipmentSize: true,
        rateHazardous: true,
        rateOrderType: true,
        rateSteamshipOrRailroad: true,
      });
    }
  }, [hideRateConditionColumns]);

  function renderFooter() {
    if (isLoading) {
      return null;
    }
    return <DataTablePagination table={table} />;
  }

  function onToggleRateConditionsVisibility() {
    setHideRateConditionColumns((visibility) => !visibility);
  }

  function renderHeader() {
    return (
      <CardHeader>
        <CardTitle className="text-lg font-semibold mb-4">
          {t('LANE_RATES')}
        </CardTitle>
        <div className="mt-2 flex justify-between flex-wrap">
          <div className="flex-1">
            <DataTableToolbar table={table}>
              <div className="flex justify-end">
                <div className="flex space-x-2 flex-wrap">
                  <Button
                    onClick={onToggleRateConditionsVisibility}
                    className={[
                      'h-10',
                      {
                        'bg-slate-50': !hideRateConditionColumns,
                      },
                    ]}
                    size="sm"
                    variant="outline"
                  >
                    {hideRateConditionColumns
                      ? t('EXPAND_RATE_CONDITIONS')
                      : t('COLLAPSE_RATE_CONDITIONS')}
                    {hideRateConditionColumns ? (
                      <ChevronRightIcon className="w-4 h-4" />
                    ) : (
                      <ChevronLeftIcon className="w-4 h-4" />
                    )}
                  </Button>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="h-10">
                        {t('ACTIONS')}{' '}
                        <ChevronDownIcon className="ml-2 w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <TooltipProvider>
                        <Tooltip
                          content={
                            canUpdate()
                              ? ''
                              : 'You do not have permission to upload'
                          }
                        >
                          <DropdownMenuItem
                            disabled={!canUpdate()}
                            onClick={() => handleBulkUploadClick()}
                          >
                            {t('UPLOAD_LANE_RATES')}
                          </DropdownMenuItem>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip
                          content={
                            canUpdate()
                              ? ''
                              : 'You do not have permission to add'
                          }
                        >
                          <DropdownMenuItem disabled={!canUpdate()}>
                            <Link
                              to="/lane-rates-v2/add"
                              className="text-black"
                            >
                              {t('ADD_NEW_LANE_RATE')}
                            </Link>
                          </DropdownMenuItem>
                        </Tooltip>
                      </TooltipProvider>
                      <DropdownMenuItem onClick={exportLaneRates}>
                        {t('EXPORT_LANE_RATES')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </DataTableToolbar>
          </div>
        </div>
        {selectedRowCount > 0 && (
          <Alert className="mt-4 text-sm bg-blue-50">
            <div className="flex justify-between items-center">
              <span>
                {selectedRowCount} {t('ROWS_SELECTED')}.
              </span>
              <Button
                disabled={!canUpdate()}
                variant="link"
                onClick={() => onBulkEditRateSequence()}
              >
                {t('EDIT_LANE_RATES')}
                <ArrowRightIcon className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </Alert>
        )}
      </CardHeader>
    );
  }

  return (
    <div className="px-4 py-5 sm:p-6 mx-auto">
      <Card>
        {renderHeader()}
        <CardContent className="space-y-4">
          <DataTable
            table={table}
            isLoading={isLoading}
            highlighted={highlighted}
          />
          {renderFooter()}
        </CardContent>
      </Card>
      <AuditTrailModal
        show={showAuditTrailModal}
        rate={rateAudit}
        onHide={() => setShowAuditTrailModal(false)}
        type="laneRates"
      />
      {showLaneRatesCsvUploadModal && (
        <LaneRatesCsvUploadModal
          onClose={() => setLaneRatesCsvUploadModalIsOpen(false)}
        />
      )}
      {visible && (
        <EditRateSequenceModal
          laneRate={selectedRateSequence}
          open={visible}
          onOpenChange={close}
          onSaveSuccess={handleResequenceRatesSuccess}
        />
      )}
    </div>
  );
}
